import {
  type FaceBounds,
  type FaceDetectionResult,
  getFaceBounds,
  PITCH_THRESHOLD,
  ROLL_THRESHOLD,
  type SimpleLandmark,
  YAW_THRESHOLD,
} from "@/features/facialRecognition";

export type FrontFacialUploadValidationReason = "no_face" | "multiple_faces" | "non_frontal_pose";

export class FrontFacialUploadValidationError extends Error {
  readonly reason: FrontFacialUploadValidationReason;

  constructor(reason: FrontFacialUploadValidationReason) {
    super(`Front facial upload failed validation: ${reason}`);
    this.name = "FrontFacialUploadValidationError";
    this.reason = reason;
  }
}

type ValidateFrontFacialUploadArgs = {
  dataUrl: string;
  detectFaceInImage: (
    imageDataUrl: string,
    options?: { maxFaces?: number },
  ) => Promise<FaceDetectionResult>;
};

// We need to distinguish "exactly one face" from "two or more faces" at the
// validation gate. The live capture path keeps `numFaces: 1` so the RAF loop
// stays cheap; here we ask for up to two faces so that the "multiple_faces"
// branch is actually reachable. Anything beyond the second face is irrelevant
// — we reject as soon as we know it's not exactly one.
const MAX_FACES_FOR_UPLOAD_VALIDATION = 2;

export type FrontFacialUploadValidationResult = {
  detectionResult: FaceDetectionResult;
  landmarks: SimpleLandmark[];
  faceBounds: FaceBounds;
};

export async function validateFrontFacialUpload({
  dataUrl,
  detectFaceInImage,
}: ValidateFrontFacialUploadArgs): Promise<FrontFacialUploadValidationResult> {
  const detectionResult = await detectFaceInImage(dataUrl, {
    maxFaces: MAX_FACES_FOR_UPLOAD_VALIDATION,
  });

  if (detectionResult.status === "initializing") {
    throw new FrontFacialUploadValidationError("no_face");
  }

  const landmarks = detectionResult.landmarks?.[0] ?? null;

  if (detectionResult.faceCount === 0 || !landmarks || landmarks.length === 0) {
    throw new FrontFacialUploadValidationError("no_face");
  }

  if (detectionResult.faceCount > 1) {
    throw new FrontFacialUploadValidationError("multiple_faces");
  }

  // Face-size is intentionally excluded from this gate. The cropper that runs
  // after this validation re-frames the face, and the server-side
  // FrontalFacialImageValidation is the authoritative quality check.
  const isFrontalPose =
    Math.abs(detectionResult.yaw) <= YAW_THRESHOLD &&
    Math.abs(detectionResult.pitch) <= PITCH_THRESHOLD &&
    Math.abs(detectionResult.roll) <= ROLL_THRESHOLD;

  if (!isFrontalPose) {
    throw new FrontFacialUploadValidationError("non_frontal_pose");
  }

  return {
    detectionResult,
    landmarks,
    faceBounds: getFaceBounds(landmarks),
  };
}
